import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isTenantAdmin } from '../lib/access';
import { useLocation } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Send, MessageCircle, Ghost, EyeOff, Plus, 
  CheckCircle, Clock, Star, ThumbsUp, Award, Tag, MessageSquare,
  RefreshCw, Zap, ChevronDown, Eye, Bell, BellOff, Search, Filter,
  Smile, AtSign, X, ArrowUpRight, AlertTriangle, Flag, ShieldCheck, Trash2,
  Users, Building2, Lock, UserRoundCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { createRealtimeSocket } from '../lib/realtime';
import EmojiPicker from 'emoji-picker-react';

const CATEGORIES = [
  { value: 'all', label: 'All', icon: '📋', color: 'slate' },
  { value: 'general', label: 'General', icon: '💬', color: 'gray', bg: 'bg-gray-500', light: 'bg-gray-100' },
  { value: 'urgent', label: 'Urgent', icon: '🔴', color: 'red', bg: 'bg-red-500', light: 'bg-red-100' },
  { value: 'process', label: 'Process', icon: '📋', color: 'blue', bg: 'bg-blue-500', light: 'bg-blue-100' },
  { value: 'technical', label: 'Technical', icon: '⚙️', color: 'purple', bg: 'bg-purple-500', light: 'bg-purple-100' },
  { value: 'policy', label: 'Policy', icon: '📜', color: 'green', bg: 'bg-green-500', light: 'bg-green-100' },
];

const FloorSpace = ({ user, setUser }) => {
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [floorLeaders, setFloorLeaders] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Quick question input
  const [quickQuestion, setQuickQuestion] = useState('');
  const [handoffDraft, setHandoffDraft] = useState(false);
  const [quickCategory, setQuickCategory] = useState('general');
  const [ghostMode, setGhostMode] = useState(false);
  const [taggedLeaders, setTaggedLeaders] = useState([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [floorSettings, setFloorSettings] = useState(null);
  const [people, setPeople] = useState([]);
  const [visibility, setVisibility] = useState('team');
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [showAudiencePicker, setShowAudiencePicker] = useState(false);
  const [moderationDialog, setModerationDialog] = useState({ open: false, question: null, reason: '' });

  // Answer feedback state
  const [feedbackMap, setFeedbackMap] = useState({}); // { answer_id: 'helpful' | 'unsound' }
  const [reportDialog, setReportDialog] = useState({ open: false, answerId: null, comment: '' });
  
  // Answer input for expanded question
  const [answerText, setAnswerText] = useState('');
  const [answerGhostMode, setAnswerGhostMode] = useState(false);
  const [showAnswerEmoji, setShowAnswerEmoji] = useState(false);
  
  // Typing indicators
  const [typingUsers, setTypingUsers] = useState({});
  
  // Category picker
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  
  const chatEndRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const quickInputRef = useRef(null);
  const draftAppliedRef = useRef(false);
  
  const isAdmin = isTenantAdmin(user);
  const isFloorLeader = user.is_floor_leader || user.role === 'floor_leader';
  const isSourceLeader = Boolean(user.is_source_lead);
  const canSeeEscalations = isFloorLeader || isSourceLeader || isAdmin;
  const canUseFloorSpace = user.permissions?.can_use_floor_space !== false;
  const canModerate = user.permissions?.floor_space_moderate === true || user.role === 'master_admin' || user.is_master_admin === true;
  useEffect(() => {
    const draftQuestion = location.state?.draftQuestion?.trim();
    if (!draftAppliedRef.current && draftQuestion) {
      draftAppliedRef.current = true;
      setQuickQuestion(draftQuestion);
      setHandoffDraft(true);
      setTimeout(() => quickInputRef.current?.focus(), 100);
    }
  }, [location.state]);

  const audienceLabels = {
    organisation: 'Organisation-wide', team: user.team_name || 'Your team',
    selected_people: 'Selected people', private: 'Private',
  };

  const availableAudiences = [
    floorSettings?.organisation_wide_posting !== false && { value: 'organisation', label: 'Organisation-wide', icon: Building2 },
    user.team_id && { value: 'team', label: user.team_name || 'Your team', icon: Users },
    floorSettings?.selected_people_posting !== false && { value: 'selected_people', label: 'Selected people', icon: UserRoundCheck },
    floorSettings?.private_posting !== false && { value: 'private', label: 'Private', icon: Lock },
  ].filter(Boolean);

  // Fetch floor leaders
  const fetchFloorLeaders = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/users/floor-leaders');
      setFloorLeaders(res.data);
    } catch (error) {
      console.error('Failed to fetch floor leaders', error);
    }
  }, []);

  const fetchPrivacyOptions = useCallback(async () => {
    try {
      const [settingsRes, peopleRes] = await Promise.all([
        axiosInstance.get('/floor/settings'), axiosInstance.get('/floor/people'),
      ]);
      setFloorSettings(settingsRes.data);
      setVisibility(settingsRes.data.default_visibility || 'team');
      setPeople(peopleRes.data || []);
    } catch (error) {
      console.error('Failed to load Floor Space privacy settings', error);
    }
  }, []);

  // Fetch escalations (Floor Leaders / Admins only)
  const fetchEscalations = useCallback(async () => {
    if (!canSeeEscalations) return;
    try {
      const res = await axiosInstance.get('/floor/escalations');
      setEscalations(res.data.filter((e) => String(e.status || '').toLowerCase() !== 'resolved'));
    } catch {}
  }, [canSeeEscalations]);

  // Fetch current user's feedback map (disables duplicate feedback submissions)
  const fetchFeedbackMap = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/floor/answers/my-feedback-map');
      setFeedbackMap(res.data || {});
    } catch (e) {
      // silent
    }
  }, []);


  // Socket.IO connection
  useEffect(() => {
    socketRef.current = createRealtimeSocket({
      auth: { token: localStorage.getItem('token') },
    });

    socketRef.current.on('connect', () => {
      setConnected(true);
      socketRef.current.emit('join_floor', { user_id: user.id, user_name: user.name, is_floor_leader: isFloorLeader });
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
    });

    // New question notification
    socketRef.current.on('new_floor_question', (question) => {
      setQuestions(prev => prev.some(q => q.id === question.id) ? prev : [question, ...prev]);
      
      // Notify floor leaders
      if (isFloorLeader && question.user_id !== user.id) {
        const notification = {
          id: question.id,
          type: 'new_question',
          title: 'New Question',
          message: question.question.substring(0, 50) + '...',
          question_id: question.id,
          timestamp: new Date().toISOString(),
          read: false
        };
        setNotifications(prev => [notification, ...prev]);
        
        // Play notification sound
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleympr6ypk3J0fX6JlZybiXJjWl1sf5Wlq5uAdWJaXGyCla+0rpmEbFlKTGB5layuoYx3Y1VPW3CLo62rmYRuXVRSYHmOoamom4RvYVlXYXeMn6akmYJtYFpcZHuOnqKhmH5pXVteZnyOn6CdlHplXF1gZ32OnaGem3pmW1xfZHuNnaCemHpkWlpfZXqMnJ+dlnljWVxfZHqMnJ+dlXliWFpeZHmMm56dlHhiWVtdY3mLm56clHdgV1pdY3iKm52blHdgWFpdYneKmp2alHZfV1pcYnaJmZyZknVeVlpbYXaImZuYkHRdVllaYHWHmJqXj3JcVVlZX3SGl5mWjnFbVFhYXnSFlpiVjXBaU1dXXXOElZeTjG9ZUlZWXHKDlJaSi25YUVVVWnGClJWSim1XUFRUWXCBk5SQiGxWUFNTWG+AkpOPh2tVT1JSV26/kZKOhmtUTlFRVm2+kJGNhWpTTVBQVWy9j5CMhGlSTU9PVGu8jpCLg2hRTE5OUmq7jY+KgmdQS01NUWm6jI6JgWZPS0xMUGi5i42IgGVOSkxLT2e4io2HfmRNSUtKTma3iYyGfWNMSEpJTWW2iIuFfGJLSElITGS1h4qEe2FKR0hHTGOzhomDel9JRkdGSmKyhYiCeV5IRUZFSWGxhIeBd11HREVESGCwg4aAdlxGQ0RDSF+vgoV/dVtFQkNCR16ugoR+dFpEQUJBRl2tgYN9c1lDQEFARVysgIJ8clhCPz9ARFurgYF7cVdBPj4/Q1qqf4B6cFZAPT0+Qlmpfn95b1U/PD09QVinfoB4blQ+Ozw8QFemfX94bVE+Ojs7PlsAAAA=');
          audio.volume = 0.3;
          audio.play().catch(() => {});
        } catch (e) {}
      }
      
      scrollToBottom();
    });

    // Floor leader answered - clear notification
    socketRef.current.on('floor_question_answered', (data) => {
      if (data.answered_by_floor_leader) {
        setNotifications(prev => prev.filter(n => n.question_id !== data.question_id));
      }
      
      setQuestions(prev => prev.map(q => {
        if (q.id === data.question_id) {
          return { ...q, status: data.new_status, has_floor_leader_answer: true };
        }
        return q;
      }));
    });

    socketRef.current.on('floor_question_update', (data) => {
      setQuestions(prev => prev.map(question => (
        question.id === data.question_id ? { ...question, ...(data.updates || {}) } : question
      )));
    });

    socketRef.current.on('floor_question_deleted', (data) => {
      setQuestions(prev => prev.filter(question => question.id !== data.question_id));
      setExpandedQuestion(prev => prev === data.question_id ? null : prev);
    });

    socketRef.current.on('floor_question_escalated', (data) => {
      setQuestions(prev => prev.map(question => (
        question.id === data.question_id ? { ...question, status: 'escalated' } : question
      )));
      fetchEscalations();
    });

    // New answer
    socketRef.current.on('new_floor_answer', (data) => {
      setQuestions(prev => prev.map(q => {
        if (q.id === data.question_id) {
          if (!data.answer || (q.answers || []).some(answer => answer.id === data.answer.id)) return q;
          const answers = [...(q.answers || []), data.answer];
          answers.sort((a, b) => {
            if (a.is_best_answer !== b.is_best_answer) return b.is_best_answer ? 1 : -1;
            if (a.is_floor_leader !== b.is_floor_leader) return b.is_floor_leader ? 1 : -1;
            return (b.upvotes || 0) - (a.upvotes || 0);
          });
          return { ...q, answers, answer_count: answers.length, status: data.new_status || q.status };
        }
        return q;
      }));
    });

    // Typing indicator
    socketRef.current.on('floor_typing', (data) => {
      if (data.user_id !== user.id) {
        setTypingUsers(prev => ({
          ...prev,
          [data.question_id || 'main']: {
            user_name: data.user_name,
            is_floor_leader: data.is_floor_leader,
            timestamp: Date.now()
          }
        }));
        
        // Clear typing indicator after 3 seconds
        setTimeout(() => {
          setTypingUsers(prev => {
            const key = data.question_id || 'main';
            if (prev[key]?.timestamp && Date.now() - prev[key].timestamp >= 2900) {
              const newState = { ...prev };
              delete newState[key];
              return newState;
            }
            return prev;
          });
        }, 3000);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_floor');
        socketRef.current.disconnect();
      }
    };
  }, [user.id, user.name, isFloorLeader]);

  useEffect(() => {
    fetchQuestions();
    fetchFloorLeaders();
    fetchEscalations();
    fetchFeedbackMap();
    fetchPrivacyOptions();
    // Polling fallback — catches messages when WebSocket connection drops
    const pollInterval = setInterval(() => {
      fetchQuestions();
    }, 15000);
    return () => clearInterval(pollInterval);
  }, [fetchFloorLeaders, fetchEscalations, fetchFeedbackMap, fetchPrivacyOptions]);

  const scrollToBottom = () => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const fetchQuestions = async () => {
    try {
      const res = await axiosInstance.get('/floor/questions');
      setQuestions(res.data);

      const linkedQuestionId = new URLSearchParams(window.location.search).get('questionId');
      if (linkedQuestionId && res.data.some(q => q.id === linkedQuestionId)) {
        setExpandedQuestion(linkedQuestionId);
      }
    } catch (error) {
      console.error('Failed to fetch questions', error);
    } finally {
      setLoading(false);
    }
  };

  // Emit typing indicator
  const emitTyping = (questionId = null) => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    socketRef.current?.emit('floor_typing', {
      user_id: user.id,
      user_name: user.name,
      is_floor_leader: isFloorLeader,
      question_id: questionId
    });
    
    typingTimeoutRef.current = setTimeout(() => {}, 2000);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this Floor Space message and its answers?')) return;
    try {
      await axiosInstance.delete(`/floor/questions/${questionId}`);
      setQuestions((current) => current.filter((question) => question.id !== questionId));
      setExpandedQuestion((current) => current === questionId ? null : current);
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };
  const handleQuickQuestion = async (e) => {
    e.preventDefault();
    if (!canUseFloorSpace) return;
    if (!quickQuestion.trim()) return;
    
    try {
      const res = await axiosInstance.post('/floor/questions', {
        question: quickQuestion,
        category: quickCategory,
        tags: taggedLeaders.map(l => l.name),
        is_ghost: ghostMode,
        is_anonymous: ghostMode,
        tagged_leader_ids: taggedLeaders.map(l => l.id),
        visibility,
        allowed_user_ids: selectedPeople,
      });
      
      setQuickQuestion('');
      setHandoffDraft(false);
      setGhostMode(false);
      setTaggedLeaders([]);
      setSelectedPeople([]);
      fetchQuestions();
      scrollToBottom();
      toast.success('Question posted!');
    } catch (error) {
      toast.error('Failed to post question');
    }
  };

  const handleSubmitAnswer = async (questionId) => {
    if (!canUseFloorSpace) return;
    if (!answerText.trim()) return;
    
    try {
      await axiosInstance.post('/floor/answers', {
        question_id: questionId,
        answer: answerText,
        is_ghost: answerGhostMode
      });
      
      setAnswerText('');
      setAnswerGhostMode(false);
      fetchQuestions();
      toast.success('Answer posted!');
    } catch (error) {
      toast.error('Failed to post answer');
    }
  };

  const handleModerate = async (updates) => {
    const { question, reason } = moderationDialog;
    if (!question || !reason.trim()) return toast.error('Enter a reason for this change');
    try {
      await axiosInstance.patch(`/floor/questions/${question.id}/moderate`, { ...updates, reason });
      setModerationDialog({ open: false, question: null, reason: '' });
      await fetchQuestions();
      toast.success('Conversation updated and audited');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Moderation failed');
    }
  };

  const handleResolve = async (questionId) => {
    try {
      await axiosInstance.patch(`/floor/questions/${questionId}/resolve`);
      // Clear notification for this question
      setNotifications(prev => prev.filter(n => n.question_id !== questionId));
      
      fetchQuestions();
      toast.success('Question resolved!');
    } catch (error) {
      toast.error('Failed to resolve');
    }
  };

  const handleMarkBest = async (answerId, questionId) => {
    try {
      await axiosInstance.patch(`/floor/answers/${answerId}/best`);
      
      // Clear notification
      setNotifications(prev => prev.filter(n => n.question_id !== questionId));
      
      fetchQuestions();
      toast.success('Marked as best answer!');
    } catch (error) {
      toast.error('Failed to mark best answer');
    }
  };

  const handleUpvote = async (answerId) => {
    try {
      await axiosInstance.patch(`/floor/answers/${answerId}/upvote`);
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const submitFeedback = async (answerId, feedbackType, comment = '') => {
    try {
      await axiosInstance.post(`/floor/answers/${answerId}/feedback`, {
        answer_id: answerId,
        feedback_type: feedbackType,
        optional_comment: comment || null,
      });
      setFeedbackMap(prev => ({ ...prev, [answerId]: feedbackType }));
      toast.success(feedbackType === 'helpful' ? 'Marked as helpful' : 'Answer reported');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to submit feedback');
    }
  };

  const handleReportAnswer = (answerId) => {
    setReportDialog({ open: true, answerId, comment: '' });
  };

  const submitReport = async () => {
    const { answerId, comment } = reportDialog;
    if (!answerId) return;
    if (comment.length > 200) {
      toast.error('Comment must be 200 characters or less');
      return;
    }
    await submitFeedback(answerId, 'unsound', comment);
    setReportDialog({ open: false, answerId: null, comment: '' });
  };

  const handleEscalate = async (questionId) => {
    try {
      await axiosInstance.post('/floor/escalations', {
        question_id: questionId,
        reason: 'Needs internal knowledge article created',
      });
      fetchQuestions();
      fetchEscalations();
      toast.success('Escalated to Source Leaders');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to escalate');
    }
  };

  const handleResolveEscalation = async (escalationId) => {
    try {
      await axiosInstance.patch(`/floor/escalations/${escalationId}/resolve`);
      await Promise.all([fetchQuestions(), fetchEscalations()]);
      toast.success('Escalation resolved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to resolve escalation');
    }
  };


  const addEmoji = (emojiData, isAnswer = false) => {
    if (isAnswer) {
      setAnswerText(prev => prev + emojiData.emoji);
      setShowAnswerEmoji(false);
    } else {
      setQuickQuestion(prev => prev + emojiData.emoji);
      setShowEmojiPicker(false);
    }
  };

  const toggleTagLeader = (leader) => {
    setTaggedLeaders(prev => {
      const exists = prev.find(l => l.id === leader.id);
      if (exists) {
        return prev.filter(l => l.id !== leader.id);
      }
      return [...prev, leader];
    });
  };

  const getCategoryStyle = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[1];
  };

  const getDisplayName = (item, isOwn) => {
    if (item.is_ghost) {
      if (isAdmin) {
        return { name: item.real_user_name || item.user_name, isGhost: true, showReal: true };
      } else if (isOwn) {
        return { name: 'You (Anonymous)', isGhost: true };
      } else {
        return { name: 'Anonymous', isGhost: true };
      }
    }
    return { name: isOwn ? 'You' : item.user_name, isGhost: false };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Filter questions
  const filteredQuestions = questions.filter(q => {
    const matchesSearch = searchQuery === '' || 
      q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.user_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || q.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const unreadNotifications = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col" data-testid="floor-space-page">
      
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageCircle className="text-sky-400" size={24} />
            <div>
              <h1 className="text-lg font-semibold text-white">Floor Space</h1>
              <p className="text-xs text-slate-400">Ask questions, get expert answers</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Notifications (Floor Leaders only) */}
            {isFloorLeader && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`p-2 rounded-full transition-colors relative ${
                    unreadNotifications > 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'
                  }`}
                >
                  {unreadNotifications > 0 ? <Bell size={18} /> : <BellOff size={18} />}
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                      {unreadNotifications}
                    </span>
                  )}
                </button>
                
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-72 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                    <div className="p-3 border-b border-slate-700 flex items-center justify-between">
                      <span className="text-sm font-medium text-white">Notifications</span>
                      {notifications.length > 0 && (
                        <button 
                          onClick={() => setNotifications([])}
                          className="text-xs text-slate-400 hover:text-white"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">
                          No new notifications
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div 
                            key={n.id}
                            onClick={() => {
                              const actionParams = n.action_url
                                ? new URLSearchParams(n.action_url.split('?')[1] || '')
                                : null;
                              const questionId = n.question_id || n.metadata?.question_id || actionParams?.get('questionId');
                              if (questionId) {
                                setExpandedQuestion(questionId);
                                window.history.replaceState(null, '', `/floor-space?questionId=${questionId}`);
                              }
                              setShowNotifications(false);
                            }}
                            className="p-3 border-b border-slate-700/50 hover:bg-slate-700/50 cursor-pointer"
                          >
                            <p className="text-sm text-white">{n.title}</p>
                            <p className="text-xs text-slate-400 truncate">{n.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Search Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-full transition-colors ${showFilters ? 'bg-sky-500 text-white' : 'bg-slate-700 text-slate-400'}`}
            >
              <Search size={18} />
            </button>
            
            {isFloorLeader && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
                <Star size={12} className="mr-1" />
                Floor Leader
              </span>
            )}
            
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              connected 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}>
              <Zap size={12} className="mr-1" />
              {connected ? 'Live' : 'Offline'}
            </span>

            <button
              onClick={() => fetchQuestions()}
              title="Refresh questions"
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-700/60 text-slate-200 border border-slate-600 hover:bg-slate-600/80 transition-colors"
              data-testid="floor-space-refresh-btn"
            >
              <RefreshCw size={12} className="mr-1" /> Refresh
            </button>
          </div>
        </div>
        
        {/* Search & Filter Bar */}
        {showFilters && (
          <div className="max-w-4xl mx-auto mt-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions..."
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            
            <div className="flex gap-1">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setCategoryFilter(cat.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    categoryFilter === cat.value 
                      ? 'bg-sky-500 text-white' 
                      : 'bg-slate-700 text-slate-400 hover:text-white'
                  }`}
                >
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* Typing indicator for new questions */}
          {typingUsers['main'] && (
            <div className="flex items-center gap-2 text-slate-400 text-sm animate-pulse">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span>
                {typingUsers['main'].user_name} is typing
                {typingUsers['main'].is_floor_leader && <Star size={10} className="inline ml-1 text-amber-400" />}
              </span>
            </div>
          )}
          
          {/* Escalation Queue — visible to Floor Leaders / Admins */}
          {canSeeEscalations && escalations.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20" data-testid="escalation-queue">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpRight size={14} className="text-orange-400" />
                <span className="text-xs font-semibold text-orange-400">{escalations.length} Escalation{escalations.length > 1 ? 's' : ''} Open</span>
              </div>
              <div className="space-y-1.5">
                {escalations.slice(0, 3).map(esc => (
                  <div key={esc.id} className="flex items-center gap-2 text-xs text-slate-400">
                    <AlertTriangle size={10} className="text-orange-400 flex-shrink-0" />
                    <span className="truncate flex-1">"{esc.question_text?.substring(0, 60)}{esc.question_text?.length > 60 ? '...' : ''}"</span>
                    <span className="text-[10px] text-slate-500">by {esc.escalated_by_name}</span>
                    {(isSourceLeader || isAdmin) && (
                      <button
                        type="button"
                        onClick={() => handleResolveEscalation(esc.id)}
                        className="text-[10px] px-2 py-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30"
                      >
                        Resolve
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {filteredQuestions.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="mx-auto text-slate-600 mb-4" size={48} />
              <p className="text-slate-400 text-lg">
                {searchQuery || categoryFilter !== 'all' ? 'No matching questions' : 'No questions yet'}
              </p>
              <p className="text-slate-500 text-sm mt-1">
                {searchQuery || categoryFilter !== 'all' ? 'Try different filters' : 'Be the first to ask!'}
              </p>
            </div>
          ) : (
            [...filteredQuestions].reverse().map(question => {
              const catStyle = getCategoryStyle(question.category);
              const isOwn = question.user_id === user.id;
              const displayInfo = getDisplayName(question, isOwn);
              const isExpanded = expandedQuestion === question.id;
              const questionTyping = typingUsers[question.id];
              
              return (
                <div key={question.id} className="space-y-2">
                  {/* Question Bubble */}
                  <div className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      displayInfo.isGhost 
                        ? 'bg-purple-500/30 border border-purple-500/50' 
                        : isOwn 
                          ? 'bg-sky-500/30 border border-sky-500/50'
                          : 'bg-slate-600 border border-slate-500'
                    }`}>
                      {displayInfo.isGhost ? (
                        <Ghost size={18} className="text-purple-400" />
                      ) : (
                        <span className="text-sm font-medium text-white">
                          {displayInfo.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    
                    <div className={`flex-1 max-w-[75%] ${isOwn ? 'items-end' : ''}`}>
                      <div className={`flex items-center gap-2 mb-1 ${isOwn ? 'justify-end' : ''}`}>
                        <span className={`text-xs font-medium ${displayInfo.isGhost ? 'text-purple-400' : 'text-slate-300'}`}>
                          {displayInfo.name}
                        </span>
                        {question.is_floor_leader && (
                          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30" data-testid="expert-badge">
                            <Star size={8} /> Expert
                          </span>
                        )}
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${catStyle.bg} text-white`}>
                          {catStyle.icon} {catStyle.label}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800/70 text-slate-300 border border-slate-600" title="Who can see this conversation">
                          <Eye size={9} /> {question.visibility === 'team' ? (question.team_name || 'Team only') : (audienceLabels[question.visibility || 'organisation'])}
                        </span>
                        {question.locked && <Lock size={12} className="text-amber-400" title="Replies are locked" />}
                        {question.hidden && <EyeOff size={12} className="text-rose-400" title="Hidden under review" />}
                        {question.status === 'resolved' && (
                          <CheckCircle size={12} className="text-green-400" />
                        )}
                        {isOwn && (
                          <button type="button" onClick={(event) => { event.stopPropagation(); handleDeleteQuestion(question.id); }}
                            className="p-1 text-slate-400 hover:text-rose-300" aria-label="Delete your message"
                            data-testid={`delete-floor-message-${question.id}`}>
                            <Trash2 size={12} />
                          </button>
                        )}
                        {question.status === 'escalated' && (
                          <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30" data-testid={`escalated-badge-${question.id}`}>
                            <ArrowUpRight size={9} />
                            Escalated
                          </span>
                        )}
                      </div>
                      
                      <div 
                        className={`relative rounded-2xl px-4 py-3 cursor-pointer transition-all ${
                          isOwn 
                            ? `${displayInfo.isGhost ? 'bg-purple-600' : 'bg-sky-600'} text-white rounded-tr-sm`
                            : `${displayInfo.isGhost ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-slate-700 border border-slate-600'} text-white rounded-tl-sm`
                        } ${isExpanded ? 'ring-2 ring-sky-400' : 'hover:ring-1 hover:ring-slate-500'}`}
                        onClick={() => setExpandedQuestion(isExpanded ? null : question.id)}
                      >
                        <p className="text-sm">{question.question}</p>
                        
                        {/* Tagged Leaders */}
                        {question.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {question.tags.map((tag, idx) => (
                              <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-amber-500/30 text-amber-300">
                                <AtSign size={8} className="mr-0.5" />{tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className={`flex items-center gap-2 mt-2 text-[10px] ${isOwn ? 'text-white/60' : 'text-slate-400'}`}>
                          <span>{formatTime(question.created_at)}</span>
                          <span>•</span>
                          <span>{question.answer_count || 0} answers</span>
                          {question.has_floor_leader_answer && (
                            <>
                              <span>•</span>
                              <span className="text-amber-400 flex items-center gap-0.5">
                                <Star size={10} /> Expert
                              </span>
                            </>
                          )}
                          <ChevronDown size={12} className={`ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Answers */}
                  {isExpanded && (
                    <div className="ml-12 space-y-2 animate-in slide-in-from-top-2">
                      {/* Typing indicator for this question */}
                      {questionTyping && (
                        <div className="flex items-center gap-2 text-slate-400 text-sm animate-pulse py-2">
                          <div className="flex gap-1">
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                          <span className="text-xs">
                            {questionTyping.user_name} is typing...
                            {questionTyping.is_floor_leader && <Star size={8} className="inline ml-1 text-amber-400" />}
                          </span>
                        </div>
                      )}
                      
                      {question.answers?.map(answer => {
                        const answerOwn = answer.user_id === user.id;
                        const answerDisplay = getDisplayName(answer, answerOwn);
                        const canMarkBest = isFloorLeader || isOwn;
                        
                        return (
                          <div key={answer.id} className={`flex gap-2 ${answerOwn ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              answer.is_floor_leader 
                                ? 'bg-amber-500/30 border border-amber-500/50'
                                : answerDisplay.isGhost 
                                  ? 'bg-purple-500/20 border border-purple-500/30'
                                  : 'bg-slate-700 border border-slate-600'
                            }`}>
                              {answer.is_floor_leader ? (
                                <Star size={14} className="text-amber-400" />
                              ) : answerDisplay.isGhost ? (
                                <Ghost size={14} className="text-purple-400" />
                              ) : (
                                <span className="text-xs font-medium text-white">
                                  {answerDisplay.name.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex-1 max-w-[70%]">
                              <div className={`flex items-center gap-2 mb-0.5 ${answerOwn ? 'justify-end' : ''}`}>
                                <span className={`text-[10px] font-medium ${
                                  answer.is_floor_leader ? 'text-amber-400' : answerDisplay.isGhost ? 'text-purple-400' : 'text-slate-400'
                                }`}>
                                  {answerDisplay.name}
                                  {answer.is_floor_leader && ' ⭐'}
                                </span>
                                {answer.is_best_answer && (
                                  <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-green-500 text-white">
                                    ✓ Best
                                  </span>
                                )}
                              </div>
                              
                              <div className={`rounded-xl px-3 py-2 ${
                                answer.is_best_answer 
                                  ? 'bg-green-500/20 border border-green-500/40'
                                  : answer.is_floor_leader 
                                    ? 'bg-amber-500/20 border border-amber-500/40'
                                    : answerOwn 
                                      ? 'bg-slate-600'
                                      : 'bg-slate-700/50 border border-slate-700'
                              } ${answerOwn ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}>
                                <p className="text-sm text-white">{answer.answer}</p>
                                
                                <div className="flex items-center gap-2 mt-1.5">
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); handleUpvote(answer.id); }}
                                    className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-sky-400"
                                    data-testid={`upvote-${answer.id}`}
                                  >
                                    <ThumbsUp size={10} /> {answer.upvotes || 0}
                                  </button>
                                  
                                  {canMarkBest && !answer.is_best_answer && (
                                    <button 
                                      onClick={(e) => { e.stopPropagation(); handleMarkBest(answer.id, question.id); }}
                                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-green-400"
                                    >
                                      <Award size={10} /> Best
                                    </button>
                                  )}

                                  {/* Helpful feedback */}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); if (!feedbackMap[answer.id]) submitFeedback(answer.id, 'helpful'); }}
                                    disabled={!!feedbackMap[answer.id]}
                                    title={feedbackMap[answer.id] === 'helpful' ? 'You marked this as helpful' : feedbackMap[answer.id] ? 'Already submitted feedback' : 'Mark as helpful'}
                                    className={`flex items-center gap-1 text-[10px] ${
                                      feedbackMap[answer.id] === 'helpful' ? 'text-emerald-400' : feedbackMap[answer.id] ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-emerald-400'
                                    }`}
                                    data-testid={`helpful-${answer.id}`}
                                  >
                                    <ShieldCheck size={10} /> Helpful
                                  </button>

                                  {/* Report unsound */}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); if (!feedbackMap[answer.id]) handleReportAnswer(answer.id); }}
                                    disabled={!!feedbackMap[answer.id]}
                                    title={feedbackMap[answer.id] === 'unsound' ? 'You reported this answer' : feedbackMap[answer.id] ? 'Already submitted feedback' : 'Report as unsound'}
                                    className={`flex items-center gap-1 text-[10px] ${
                                      feedbackMap[answer.id] === 'unsound' ? 'text-rose-400' : feedbackMap[answer.id] ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-rose-400'
                                    }`}
                                    data-testid={`report-${answer.id}`}
                                  >
                                    <Flag size={10} /> Report
                                  </button>

                                  <span className="text-[10px] text-slate-500 ml-auto">
                                    {formatTime(answer.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Answer Input */}
                      {canUseFloorSpace && !question.locked && !question.hidden && !['resolved', 'escalated'].includes(question.status) && (
                        <div className="flex gap-2 mt-3 relative">
                          <button
                            onClick={() => setAnswerGhostMode(!answerGhostMode)}
                            className={`p-2 rounded-full transition-colors ${
                              answerGhostMode ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-400'
                            }`}
                          >
                            {answerGhostMode ? <EyeOff size={16} /> : <Ghost size={16} />}
                          </button>
                          
                          <button
                            onClick={() => setShowAnswerEmoji(!showAnswerEmoji)}
                            className="p-2 rounded-full bg-slate-700 text-slate-400 hover:text-white"
                          >
                            <Smile size={16} />
                          </button>
                          
                          {showAnswerEmoji && (
                            <div className="absolute bottom-14 left-10 z-50">
                              <EmojiPicker 
                                onEmojiClick={(emoji) => addEmoji(emoji, true)} 
                                theme="dark"
                                width={300}
                                height={350}
                              />
                            </div>
                          )}
                          
                          <input
                            type="text"
                            value={answerText}
                            onChange={(e) => {
                              setAnswerText(e.target.value);
                              emitTyping(question.id);
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && handleSubmitAnswer(question.id)}
                            placeholder={answerGhostMode ? "Answer anonymously..." : "Write your answer..."}
                            className={`flex-1 px-4 py-2 rounded-full text-sm bg-slate-700 border text-white placeholder-slate-400 focus:outline-none focus:ring-2 ${
                              answerGhostMode ? 'border-purple-500/50 focus:ring-purple-500' : 'border-slate-600 focus:ring-sky-500'
                            }`}
                          />
                          
                          <button
                            onClick={() => handleSubmitAnswer(question.id)}
                            disabled={!answerText.trim()}
                            className={`p-2 rounded-full disabled:opacity-50 ${
                              answerGhostMode ? 'bg-purple-500' : 'bg-sky-500'
                            } text-white`}
                          >
                            <Send size={16} />
                          </button>
                        </div>
                      )}
                      
                      {(isOwn && (question.answer_count || 0) === 0) && (
                        <button onClick={() => handleDeleteQuestion(question.id)} className="ml-10 mt-2 flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300">
                          <Trash2 size={12} /> Delete question
                        </button>
                      )}
                      {canModerate && (
                        <button onClick={() => setModerationDialog({ open: true, question, reason: '' })} className="ml-10 mt-2 flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300">
                          <ShieldCheck size={12} /> Privacy & moderation
                        </button>
                      )}
                      {isFloorLeader && question.status !== 'resolved' && question.status !== 'escalated' && (
                        <button
                          onClick={() => handleEscalate(question.id)}
                          className="ml-10 mt-2 flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300"
                          data-testid={`escalate-btn-${question.id}`}
                        >
                          <ArrowUpRight size={12} /> Escalate to Source Leader
                        </button>
                      )}
                      {isFloorLeader && question.status !== 'resolved' && (
                        <button
                          onClick={() => handleResolve(question.id)}
                          className="ml-10 mt-1 flex items-center gap-1 text-xs text-green-400 hover:text-green-300"
                        >
                          <CheckCircle size={12} /> Mark as Resolved
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Input Area */}
      {canUseFloorSpace && <div className="bg-slate-800/80 backdrop-blur border-t border-slate-700 px-4 py-4">
        <form onSubmit={handleQuickQuestion} className="max-w-4xl mx-auto">
          {handoffDraft && (
            <div className="mb-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-200" data-testid="floor-handoff-draft-notice">
              Draft copied from Submit Query. Review or edit it, then press Send when you are ready.
            </div>
          )}
          {/* Tagged Leaders */}
          {taggedLeaders.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {taggedLeaders.map(leader => (
                <span key={leader.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  <AtSign size={10} />{leader.name}
                  <button onClick={() => toggleTagLeader(leader)} className="ml-1 hover:text-white">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          <div className="relative mb-2">
            <button type="button" onClick={() => floorSettings?.users_can_change_visibility !== false && setShowAudiencePicker(!showAudiencePicker)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-700 text-xs text-slate-200 border border-slate-600" title="Choose who can see this question">
              <Eye size={13} /> Visible to: {audienceLabels[visibility]}
              {floorSettings?.users_can_change_visibility !== false && <ChevronDown size={12} />}
            </button>
            {showAudiencePicker && (
              <div className="absolute bottom-10 left-0 z-30 w-72 rounded-xl border border-slate-600 bg-slate-800 p-2 shadow-xl">
                {availableAudiences.map(option => {
                  const Icon = option.icon;
                  return <button key={option.value} type="button" onClick={() => { setVisibility(option.value); setShowAudiencePicker(false); }} className={`w-full flex items-start gap-2 rounded-lg px-3 py-2 text-left text-sm ${visibility === option.value ? 'bg-sky-500/20 text-sky-300' : 'text-slate-300 hover:bg-slate-700'}`}>
                    <Icon size={15} className="mt-0.5" /><span>{option.label}<span className="block text-[10px] text-slate-500">{option.value === 'organisation' ? 'Everyone with Floor Space access' : option.value === 'team' ? 'Members of your team' : option.value === 'selected_people' ? 'Only people you choose' : 'Only you and authorised private viewers'}</span></span>
                  </button>;
                })}
              </div>
            )}
            {visibility === 'selected_people' && (
              <div className="mt-2 flex flex-wrap gap-1 rounded-lg border border-slate-700 bg-slate-900/40 p-2 max-h-24 overflow-y-auto">
                {people.map(person => <label key={person.id} className="inline-flex items-center gap-1 rounded bg-slate-700 px-2 py-1 text-[11px] text-slate-200 cursor-pointer"><input type="checkbox" checked={selectedPeople.includes(person.id)} onChange={() => setSelectedPeople(prev => prev.includes(person.id) ? prev.filter(id => id !== person.id) : [...prev, person.id])} />{person.name}</label>)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Ghost Mode */}
            <button
              type="button"
              onClick={() => setGhostMode(!ghostMode)}
              className={`p-2.5 rounded-full transition-all ${
                ghostMode ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-400'
              }`}
              title="Ask Anonymously"
            >
              {ghostMode ? <EyeOff size={20} /> : <Ghost size={20} />}
            </button>
            
            {/* Category Picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCategoryPicker(!showCategoryPicker)}
                className={`p-2.5 rounded-full ${getCategoryStyle(quickCategory).bg} text-white`}
              >
                <Tag size={20} />
              </button>
              
              {showCategoryPicker && (
                <div className="absolute bottom-14 left-0 bg-slate-800 border border-slate-700 rounded-xl shadow-xl p-2 z-10">
                  {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => { setQuickCategory(cat.value); setShowCategoryPicker(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm ${
                        quickCategory === cat.value ? `${cat.bg} text-white` : 'text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Tag Floor Leader */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTagPicker(!showTagPicker)}
                className={`p-2.5 rounded-full ${taggedLeaders.length > 0 ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-400'}`}
                title="Tag Floor Leader"
              >
                <AtSign size={20} />
              </button>
              
              {showTagPicker && (
                <div className="absolute bottom-14 left-0 bg-slate-800 border border-slate-700 rounded-xl shadow-xl p-2 min-w-[200px] z-10">
                  <p className="text-xs text-slate-400 px-2 mb-2">Tag Floor Leaders</p>
                  {floorLeaders.length === 0 ? (
                    <p className="text-sm text-slate-500 px-2 py-2">No floor leaders available</p>
                  ) : (
                    floorLeaders.map(leader => (
                      <button
                        key={leader.id}
                        type="button"
                        onClick={() => toggleTagLeader(leader)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm ${
                          taggedLeaders.find(l => l.id === leader.id) 
                            ? 'bg-amber-500/20 text-amber-400' 
                            : 'text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <Star size={14} className="text-amber-400" />
                        {leader.name}
                        {taggedLeaders.find(l => l.id === leader.id) && <CheckCircle size={12} className="ml-auto" />}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* Emoji Picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-2.5 rounded-full bg-slate-700 text-slate-400 hover:text-white"
              >
                <Smile size={20} />
              </button>
              
              {showEmojiPicker && (
                <div className="absolute bottom-14 left-0 z-50">
                  <EmojiPicker 
                    onEmojiClick={(emoji) => addEmoji(emoji, false)} 
                    theme="dark"
                    width={300}
                    height={400}
                  />
                </div>
              )}
            </div>
            
            {/* Input */}
            <input
              ref={quickInputRef}
              type="text"
              value={quickQuestion}
              onChange={(e) => {
                setQuickQuestion(e.target.value);
                emitTyping();
              }}
              placeholder={ghostMode ? "Ask anonymously..." : "Ask a question..."}
              className={`flex-1 px-5 py-3 rounded-full text-sm bg-slate-700 border text-white placeholder-slate-400 focus:outline-none focus:ring-2 ${
                ghostMode ? 'border-purple-500/50 focus:ring-purple-500' : 'border-slate-600 focus:ring-sky-500'
              }`}
            />
            
            {/* Send */}
            <button
              type="submit"
              disabled={!quickQuestion.trim() || (visibility === 'selected_people' && selectedPeople.length === 0)}
              className={`p-3 rounded-full disabled:opacity-50 ${
                ghostMode ? 'bg-purple-500' : 'bg-sky-500'
              } text-white`}
            >
              <Send size={20} />
            </button>
          </div>
          
          {/* Helper Text */}
          <div className="flex items-center justify-center gap-4 mt-2 text-[10px] text-slate-500">
            <span>{getCategoryStyle(quickCategory).icon} {getCategoryStyle(quickCategory).label}</span>
            {ghostMode && <span className="text-purple-400">🔒 Anonymous</span>}
            {taggedLeaders.length > 0 && <span className="text-amber-400">@ {taggedLeaders.length} tagged</span>}
          </div>
        </form>
      </div>}

      <Dialog open={moderationDialog.open} onOpenChange={(open) => setModerationDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md bg-slate-900 border border-slate-700 text-white">
          <DialogHeader><DialogTitle>Privacy & moderation</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">Every change is recorded in the access history. A reason is required.</p>
          <textarea value={moderationDialog.reason} onChange={(e) => setModerationDialog(prev => ({ ...prev, reason: e.target.value }))} placeholder="Reason for this change" rows={3} className="w-full rounded-md border border-slate-700 bg-slate-800 p-2 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => handleModerate({ locked: !moderationDialog.question?.locked })}>{moderationDialog.question?.locked ? 'Unlock replies' : 'Lock replies'}</Button>
            <Button onClick={() => handleModerate({ hidden: !moderationDialog.question?.hidden })}>{moderationDialog.question?.hidden ? 'Restore' : 'Hide'}</Button>
            <Button variant="outline" onClick={() => handleModerate({ visibility: 'team' })}>Team only</Button>
            <Button variant="outline" onClick={() => handleModerate({ visibility: 'private' })}>Make private</Button>
            <Button variant="destructive" className="col-span-2" onClick={() => handleDeleteQuestion(moderationDialog.question?.id)}>Remove conversation</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Answer Dialog */}
      <Dialog open={reportDialog.open} onOpenChange={(open) => setReportDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md bg-slate-900 border border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-300">
              <Flag size={16} /> Report this answer as unsound
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400 -mt-2">
            Your report is visible only to Source Leaders and Admins. Optional comment (max 200 chars).
          </p>
          <textarea
            value={reportDialog.comment}
            onChange={(e) => setReportDialog(prev => ({ ...prev, comment: e.target.value.slice(0, 200) }))}
            placeholder="Short reason (optional)..."
            rows={3}
            maxLength={200}
            className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-rose-400"
            data-testid="report-comment-input"
          />
          <div className="text-[10px] text-slate-500 text-right">{reportDialog.comment.length}/200</div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="border-slate-600 text-slate-200 bg-slate-800 hover:bg-slate-700" onClick={() => setReportDialog({ open: false, answerId: null, comment: '' })}>
              Cancel
            </Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={submitReport} data-testid="report-submit-btn">
              Submit Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FloorSpace;
